/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import { Logout } from "@mui/icons-material";
import useAuthStore from "store/authStore";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;

  const [openMenu, setOpenMenu] = useState(false);
  const [promotionCode, setPromotionCode] = useState();
  const [loading, setLoading] = useState(true);

  const [tasksIncomplete, setTasksIncomplete] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // Update transparent state of navbar on scroll
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    // ✅ Robust check for visitedLinks status
    const checkVisitedLinks = () => {
      try {
        const visitedLinksRaw = localStorage.getItem("visitedLinks");
        if (!visitedLinksRaw) {
          setTasksIncomplete(true); // No data → assume incomplete
          return;
        }

        const visitedLinks = JSON.parse(visitedLinksRaw);
        const links = visitedLinks?.links;

        if (!links || typeof links !== "object") {
          setTasksIncomplete(true);
          return;
        }

        const linkEntries = Object.entries(links);

        // Ensure exactly 20 tasks
        const has20Tasks = linkEntries.length === 20;

        // Ensure all keys 1–20 exist and are strictly true
        const allTasksCompleted =
          has20Tasks &&
          Array.from({ length: 20 }, (_, i) => {
            const key = (i + 1).toString();
            return links[key] === true;
          }).every(Boolean);

        setTasksIncomplete(!allTasksCompleted);
      } catch (error) {
        console.error("Error parsing visitedLinks:", error);
        setTasksIncomplete(true);
      }
    };

    checkVisitedLinks();

    // Fetch referral code (with artificial delay to show loader)
    setTimeout(() => {
      const code = localStorage.getItem("referral_code");
      setPromotionCode(code || "No Code Found");
      setLoading(false);
    }, 500);

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // Handlers
  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleOpenDialog = () => {
    if (tasksIncomplete) {
      setOpenDialog(true); // Show warning if incomplete
    } else {
      logout(); // Direct logout if all 20 complete
    }
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const logout = () => {
    localStorage.clear(); // clear all user data
    useAuthStore.getState().clearAuth(); // reset auth store
    navigate("/login");
  };

  // Notifications dropdown menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  // Navbar icon styles
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <>
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
            <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          </MDBox>

          {/* Right side of Navbar */}
          {isMini ? null : (
            <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
              {/* Promotion Code */}
              <MDBox pr={1}>
                {loading ? (
                  <CircularProgress size={24} color="primary" />
                ) : (
                  <MDInput
                    label="Promotion Code"
                    value={promotionCode}
                    InputLabelProps={{ shrink: true }}
                    disabled
                  />
                )}
              </MDBox>

              {/* User + Menu + Logout */}
              <MDBox color={light ? "white" : "inherit"}>
                {/* Profile */}
                <Link to="/profile">
                  <IconButton sx={navbarIconButton} size="small" disableRipple>
                    <Icon sx={iconsStyle}>account_circle</Icon>
                  </IconButton>
                </Link>

                {/* Collapse / Expand sidebar */}
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon sx={iconsStyle} fontSize="medium">
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </IconButton>

                {/* Logout button with Tooltip */}
                <Tooltip
                  title={
                    tasksIncomplete
                      ? "Please complete all 20 tasks to logout, otherwise they will reset for today."
                      : "Logout"
                  }
                  placement="top"
                >
                  <IconButton
                    sx={navbarIconButton}
                    size="medium"
                    disableRipple
                    onClick={handleOpenDialog}
                  >
                    <Logout style={{ color: "#f44336" }} sx={iconsStyle} />
                  </IconButton>
                </Tooltip>

                {renderMenu()}
              </MDBox>
            </MDBox>
          )}
        </Toolbar>
      </AppBar>

      {/* Logout Warning Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            padding: "16px",
            backgroundColor: darkMode ? "#42424a" : "#ffffff",
          },
        }}
      >
        <DialogTitle id="logout-dialog-title" sx={{ fontWeight: "bold", color: "#f44336" }}>
          Incomplete Tasks Warning
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="logout-dialog-description"
            sx={{ color: darkMode ? "#ffffff" : "#333333" }}
          >
            Please complete tasks 1 to 20 to logout, otherwise your tasks will be reset for today.
            Are you sure you want to proceed with logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: darkMode ? "#ffffff" : "#333333",
              "&:hover": { backgroundColor: darkMode ? "#555555" : "#f0f0f0" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              logout();
              handleCloseDialog();
            }}
            sx={{
              backgroundColor: "#3279a8",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#d32f2f" },
            }}
            autoFocus
          >
            Logout Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Default props
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
